'use client';

import { useState } from 'react';
import Link from 'next/link';
import classNames from 'classnames';
import { Formik, Form, FormikProps } from 'formik';
import { feedbackFormValidationSchema } from 'app/utility/validation';
import FormFieldText from 'app/components/common/FormFieldText';
import FormFieldTextarea from 'app/components/common/FormFieldTextarea';
import {
  IFeedbackCreateResponse,
  IFeedbackFormFields,
  IFeedbackPostFormFields
} from 'app/interfaces/forms';
import WithFormFieldCheckable from 'app/components/common/WithFormFieldCheckable';
import useFeedbackModalStore from './store';
import { defaultSubject } from 'app/utility/forms';
import { IFile } from 'app/interfaces/common';

interface IFormFields extends IFeedbackFormFields {
  comment: string;
}

export default function FeedbackForm({
  placeholder,
  privacy_policy_file
}: {
  placeholder?: string | null;
  privacy_policy_file?: IFile | null;
}) {
  const { subject, setSubject } = useFeedbackModalStore();
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showError, setShowError] = useState(false);

  const initialValues = {
    name: '',
    phone: '',
    comment: subject,
    policy: false,
    mailing: false
  };

  async function submit(values: IFormFields, resetForm?: () => void) {
    try {
      setIsLoading(true);
      setIsSuccess(false);
      setShowError(false);
      const postValues: IFeedbackPostFormFields = {
        name: values.name,
        phone: values.phone,
        comment: values.comment,
        subject: subject || defaultSubject,
        mailing: values.mailing
      };
      const res = await fetch('/api/feedback', {
        method: 'POST',
        body: JSON.stringify(postValues)
      });
      const data = (await res.json()) as IFeedbackCreateResponse;
      if (!data.id) throw new Error('Feddback create error');
      resetForm?.();
      setSubject('');
      setIsSuccess(true);
    } catch (error) {
      console.log(error);
      setIsLoading(false);
      setIsSuccess(false);
      setShowError(true);
      setTimeout(() => {
        setShowError(false);
      }, 3000);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="relative">
      <Formik
        initialValues={initialValues}
        validationSchema={feedbackFormValidationSchema}
        validateOnMount={true}
        onSubmit={(values, { resetForm }) => submit(values, resetForm)}
        validateOnChange={true}
      >
        {({ isValid }: FormikProps<IFormFields>) => (
          <Form className="flex flex-col gap-2 md:grid md:grid-cols-2 md:gap-3">
            <FormFieldTextarea
              name="comment"
              autosize={false}
              className="w-full border-brown-20 focus:border-brown [&.has-value]:border-brown min-h-[260px] md:min-h-full no-scrollbar"
              placeholder={
                placeholder ||
                'Напишите, на какую тренировку вы бы хотели попасть, или какую услугу хотели бы посетить'
              }
            />
            <div className="flex flex-col gap-2">
              <FormFieldText
                name="name"
                placeholder="Ваше имя"
                className="w-full border-brown-20 focus:border-brown [&.has-value]:border-brown"
              />
              <FormFieldText
                name="phone"
                mask="+{7} (000) 000-00-00"
                placeholder="+7 (      )"
                prepareValue={value => {
                  const cleanValue = value.replace(/\D+/g, '');
                  if (cleanValue.length > 10) {
                    return cleanValue.replace(/^[78]/, '');
                  }
                  return cleanValue;
                }}
                className="w-full border-brown-20 focus:border-brown [&.has-value]:border-brown"
              />
              <div className="mt-2 xl:mt-4 flex flex-col gap-1.5 xl:gap-2.5">
                <WithFormFieldCheckable name="policy">
                  {({ field, checked }) => (
                    <label className="flex gap-5 items-center text-xs cursor-pointer">
                      <input
                        {...field}
                        checked={checked}
                        type="checkbox"
                        className="hidden peer"
                      />
                      <span
                        className={classNames(
                          'w-8 h-8 flex justify-center items-center flex-shrink-0 rounded',
                          'border border-brown-20 peer-checked:border-brown transition-colors',
                          "after:content-[''] after:w-1 after:h-1 after:rounded-full after:bg-brown after:opacity-0 peer-checked:after:opacity-100"
                        )}
                      ></span>
                      {privacy_policy_file?.url ? (
                        <span>
                          Я даю согласие на{' '}
                          <Link
                            href={privacy_policy_file?.url}
                            target="_blank"
                            className="underline"
                          >
                            обработку <br /> моих персональных даных
                          </Link>
                        </span>
                      ) : (
                        <span>
                          Я даю согласие на обработку <br /> моих персональных
                          даных
                        </span>
                      )}
                    </label>
                  )}
                </WithFormFieldCheckable>
                <WithFormFieldCheckable name="mailing">
                  {({ field, checked }) => (
                    <label className="flex gap-5 items-center text-xs cursor-pointer">
                      <input
                        {...field}
                        checked={checked}
                        type="checkbox"
                        className="hidden peer"
                      />
                      <span
                        className={classNames(
                          'w-8 h-8 flex justify-center items-center flex-shrink-0 rounded',
                          'border border-brown-20 peer-checked:border-brown transition-colors',
                          "after:content-[''] after:w-1 after:h-1 after:rounded-full after:bg-brown after:opacity-0 peer-checked:after:opacity-100"
                        )}
                      ></span>
                      <span>
                        Я даю согласие на рассылку <br /> информационных
                        материалов
                      </span>
                    </label>
                  )}
                </WithFormFieldCheckable>
              </div>
              <div className="mt-2 xl:mt-4">
                <button
                  type="submit"
                  disabled={!isValid}
                  className={classNames(
                    'button button-press p-4 text-xs/[1em] uppercase rounded border w-full transition-all duration-400',
                    {
                      'border-brown-20': !isValid,
                      'border-brown hover:bg-green hover:text-white hover:border-white':
                        isValid
                    }
                  )}
                >
                  перезвоните мне
                </button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
      {isLoading && (
        <div className="absolute inset-0 flex justify-center items-center">
          <div className="absolute inset-0 bg-graphite-dark opacity-80 rounded"></div>
          <span className="spinner w-14 h-14 border-2 border-brown"></span>
        </div>
      )}
      {!isLoading && isSuccess && (
        <div className="absolute inset-0 flex items-center bg-graphite-dark">
          <div className="flex flex-col gap-4 px-3">
            <div className="text-h1">Спасибо!</div>
            <div className="">
              Ваша заявка успешно отправлена. <br /> Наш менеджер свяжется
              с&nbsp;вами в&nbsp;ближайшее время
            </div>
          </div>
        </div>
      )}
      {showError && (
        <div className="absolute inset-0 flex items-center bg-graphite-dark">
          <div className="flex flex-col gap-4 px-3">
            <div className="text-h1">Ошибка!</div>
            <div className="">
              Что-то пошло не так. <br /> Пожалуйста, попробуйте еще раз
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
